import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import {
  Icon, naira, nairaK, API, APP_LOGO, APP_LOGO_WHITE,
  NetworkLogoAvatar, HeroLineChart, NetworkDonut, PROVIDERS, providerMeta,
} from "./lib/core";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const entApi = axios.create({ baseURL: `${BACKEND_URL}/api/enterprise`, withCredentials: true });
entApi.interceptors.request.use((config) => {
  const t = localStorage.getItem("topit_enterprise_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: "dashboard" },
  { id: "airtime", label: "Airtime Top-Up", icon: "smartphone" },
  { id: "data", label: "Data Bundles", icon: "wifi" },
  { id: "members", label: "Departments", icon: "groups" },
  { id: "schedules", label: "Schedules", icon: "schedule" },
  { id: "transactions", label: "Transactions", icon: "receipt_long" },
  { id: "analytics", label: "Analytics", icon: "monitoring" },
  { id: "team", label: "Team", icon: "manage_accounts" },
  { id: "wallet", label: "Wallet", icon: "account_balance_wallet" },
];

const ROLE_RANK = { viewer: 1, manager: 2, owner: 3 };
const can = (role, min) => (ROLE_RANK[role] || 0) >= (ROLE_RANK[min] || 99);
const fmtDate = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const STATUS_COLOR = {
  success: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700",
  failed: "bg-red-100 text-red-700", skipped: "bg-slate-100 text-slate-500",
};

const Loader = () => <div className="flex justify-center py-20"><Icon name="progress_activity" className="animate-spin text-4xl text-red-600" /></div>;
const toast = (msg, ok = true) => {
  const el = document.createElement("div");
  el.textContent = msg;
  el.className = `fixed top-5 right-5 z-[100] px-4 py-3 rounded-xl text-white text-sm font-semibold shadow-lg ${ok ? "bg-slate-900" : "bg-red-600"}`;
  el.setAttribute("data-testid", "ent-toast");
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 2800);
};

const downloadCSV = (filename, rows) => {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

// Heavy export libs (xlsx ~7MB, jspdf) are dynamically imported only on-demand
// so they stay out of the enterprise app chunk and the main customer bundle.
const downloadXLSX = async (filename, sheetName, rows) => {
  const XLSX = await import("xlsx");
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, filename);
};

const exportAnalyticsPDF = async (entName, a) => {
  const { default: jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");
  const doc = new jsPDF();
  doc.setFontSize(18); doc.setTextColor(225, 29, 46);
  doc.text("TopIt Enterprise — Analytics Report", 14, 18);
  doc.setFontSize(11); doc.setTextColor(40);
  doc.text(`${entName}`, 14, 26);
  doc.setFontSize(9); doc.setTextColor(120);
  doc.text(`Generated ${new Date().toLocaleString()}`, 14, 32);
  autoTable(doc, { startY: 38, head: [["Metric", "Value"]], body: [
    ["Total Spent", naira(a.summary.total_spent)],
    ["Total Saved (discounts)", naira(a.summary.total_saved)],
    ["Total Top-Ups", String(a.summary.total_topups)],
  ], theme: "grid", headStyles: { fillColor: [11, 18, 32] } });
  autoTable(doc, { head: [["Department", "Spent"]], body: a.by_department.map((d) => [d.name, naira(d.spent)]),
    theme: "striped", headStyles: { fillColor: [225, 29, 46] } });
  autoTable(doc, { head: [["Member", "Phone", "Department", "Top-Ups", "Spent"]],
    body: a.top_members.map((m) => [m.name, m.phone, m.department, String(m.topups), naira(m.spent)]),
    theme: "striped", headStyles: { fillColor: [11, 18, 32] } });
  doc.save(`topit-enterprise-analytics-${Date.now()}.pdf`);
};

// ==================== AUTH ====================
const EnterpriseAuth = ({ onAuthed }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await entApi.post("/login", form);
      localStorage.removeItem("topit_enterprise_token"); // session now in httpOnly cookie
      onAuthed();
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6"
      style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(225,29,46,0.15), transparent 40%)" }}>
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
        <div className="text-center mb-6">
          <img src={APP_LOGO} alt="TopIt" className="h-9 mx-auto" data-testid="ent-logo" />
          <p className="text-slate-400 text-[11px] tracking-wide mt-1.5">Enterprise · Powered by Sineli</p>
          <p className="text-slate-500 text-sm mt-3">Sign in to your enterprise portal</p>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-xl text-sm mb-4" data-testid="ent-auth-error">{error}</div>}
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="ent-email" type="email" value={form.email} onChange={set("email")} placeholder="Work email" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
          <input data-testid="ent-password" type="password" value={form.password} onChange={set("password")} placeholder="Password" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
          <button data-testid="ent-submit" type="submit" disabled={loading} className="w-full h-11 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50">
            {loading ? "Please wait..." : "Sign in"}
          </button>
        </form>
        <p className="text-center text-xs text-slate-400 mt-5">Enterprise accounts are provisioned by TopIt. Contact your administrator for access.</p>
      </div>
    </div>
  );
};

// ==================== SHARED UI ====================
const StatCard = ({ icon, label, value, tone = "slate", testid }) => {
  const tones = { slate: "bg-slate-100 text-slate-600", red: "bg-red-100 text-red-600", green: "bg-green-100 text-green-600", blue: "bg-blue-100 text-blue-600", amber: "bg-amber-100 text-amber-600" };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tones[tone]}`}><Icon name={icon} /></div>
      <p className="text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
};

const StatusBadge = ({ status }) => (
  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_COLOR[status] || "bg-slate-100 text-slate-500"}`}>{status}</span>
);

const Modal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid={testid}>
    <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white">
        <h3 className="font-bold text-slate-900">{title}</h3>
        <button onClick={onClose} data-testid="ent-modal-close" className="text-slate-400 hover:text-slate-700"><Icon name="close" /></button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

// ==================== DASHBOARD (Business Mode) ====================
const GROUP_TONES = [
  { bg: "bg-red-100", ic: "text-red-600" },
  { bg: "bg-violet-100", ic: "text-violet-600" },
  { bg: "bg-emerald-100", ic: "text-emerald-600" },
  { bg: "bg-amber-100", ic: "text-amber-600" },
  { bg: "bg-blue-100", ic: "text-blue-600" },
  { bg: "bg-pink-100", ic: "text-pink-600" },
];
const AMOUNT_PRESETS = [500, 1000, 2000, 5000];

const GroupTopUpModal = ({ group, members, onClose, onDone }) => {
  const [service, setService] = useState("airtime");
  const [amount, setAmount] = useState(1000);
  const [custom, setCustom] = useState("");
  const [plans, setPlans] = useState({});
  const [network, setNetwork] = useState("");
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const groupMembers = members.filter((m) => m.department_id === group.id);

  useEffect(() => {
    axios.get(`${API}/subscriptions`).then((r) => setPlans(r.data.providers || {})).catch(() => {});
    const counts = {};
    groupMembers.forEach((m) => { if (m.provider) counts[m.provider] = (counts[m.provider] || 0) + 1; });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
    if (top) setNetwork(top[0]);
  }, []); // eslint-disable-line

  const dataPlans = plans[network]?.data?.plans || [];
  const eligible = service === "data" ? groupMembers.filter((m) => m.provider === network) : groupMembers;
  const effAmount = service === "data" ? (plan?.amount || 0) : (custom ? Number(custom) : amount);
  const networks = Object.keys(plans);

  const submit = async () => {
    if (service === "data" && !plan) { toast("Select a data plan", false); return; }
    if (service === "airtime" && (!effAmount || effAmount < 50)) { toast("Enter a valid amount (min ₦50)", false); return; }
    if (eligible.length === 0) { toast(service === "data" ? `No ${network} members in this group` : "This group has no members", false); return; }
    setBusy(true);
    try {
      const items = eligible.map((m) => ({ member_id: m.id, amount: effAmount, ...(service === "data" ? { subcode: plan.subCode } : {}) }));
      const r = await entApi.post("/topup", { service_type: service, items });
      const ok = r.data?.summary?.successful ?? r.data?.summary?.success ?? 0;
      toast(`Sent to ${ok}/${items.length} in ${group.name} 🎉`);
      onDone();
    } catch (e) {
      toast(e.response?.data?.detail || "Top-up failed", false);
    } finally { setBusy(false); }
  };

  return (
    <Modal title={`Top up ${group.name}`} onClose={onClose} testid="ent-group-topup-modal">
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button onClick={() => setService("airtime")} data-testid="ent-group-airtime"
          className={`py-2.5 rounded-xl text-sm font-bold border ${service === "airtime" ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-600 border-slate-200"}`}>Airtime</button>
        <button onClick={() => setService("data")} data-testid="ent-group-data"
          className={`py-2.5 rounded-xl text-sm font-bold border ${service === "data" ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-600 border-slate-200"}`}>Data</button>
      </div>

      {service === "airtime" ? (
        <>
          <p className="text-sm text-slate-500 mb-3">Send airtime to all <b>{groupMembers.length}</b> members of <b>{group.name}</b>.</p>
          <p className="text-xs font-semibold text-slate-500 mb-2">Select Amount</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {AMOUNT_PRESETS.map((a) => (
              <button key={a} onClick={() => { setAmount(a); setCustom(""); }} data-testid={`ent-group-amount-${a}`}
                className={`px-3 py-2 rounded-lg text-sm font-bold border ${!custom && amount === a ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-600 border-slate-200"}`}>{naira(a)}</button>
            ))}
          </div>
          <input value={custom} onChange={(e) => setCustom(e.target.value.replace(/\D/g, ""))} data-testid="ent-group-amount-custom"
            placeholder="Or enter custom amount (₦)" inputMode="numeric"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-500 mb-4" />
        </>
      ) : (
        <>
          <p className="text-sm text-slate-500 mb-3">Data plans are per-network. Pick a network and plan — it goes to members of <b>{group.name}</b> on that network.</p>
          <p className="text-xs font-semibold text-slate-500 mb-2">Network</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {(networks.length ? networks : ["MTN", "Airtel", "Glo", "9mobile"]).map((n) => {
              const cnt = groupMembers.filter((m) => m.provider === n).length;
              return (
                <button key={n} onClick={() => { setNetwork(n); setPlan(null); }} data-testid={`ent-group-net-${n}`}
                  className={`px-3 py-2 rounded-lg text-sm font-bold border ${network === n ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>{n} <span className="opacity-60">({cnt})</span></button>
              );
            })}
          </div>
          <p className="text-xs font-semibold text-slate-500 mb-2">Plan</p>
          <select value={plan?.subCode || ""} onChange={(e) => setPlan(dataPlans.find((p) => p.subCode === e.target.value) || null)} data-testid="ent-group-plan"
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-500 mb-4">
            <option value="">{dataPlans.length ? "Select a data plan" : "No plans for this network"}</option>
            {dataPlans.map((p, i) => <option key={`${p.subCode}-${i}`} value={p.subCode}>{p.description} — {naira(p.amount)}</option>)}
          </select>
        </>
      )}

      <button onClick={submit} disabled={busy || eligible.length === 0} data-testid="ent-group-topup-submit"
        className="w-full bg-gradient-to-r from-red-600 to-red-500 text-white font-bold py-3.5 rounded-xl disabled:opacity-60 flex items-center justify-center gap-2">
        <Icon name="bolt" />{busy ? "Sending…" : `Send ${naira(effAmount || 0)} to ${eligible.length} member${eligible.length === 1 ? "" : "s"}`}</button>
    </Modal>
  );
};


const BizStat = ({ icon, label, value, sub, cta, onCta, tone, testid }) => {
  const tones = { red: "bg-red-50 text-red-600", violet: "bg-violet-50 text-violet-600", green: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600" };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 font-medium">{label}</p>
          <p className="text-2xl font-extrabold text-slate-900 mt-1 tnum">{value}</p>
        </div>
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}><Icon name={icon} /></span>
      </div>
      <p className="text-xs text-slate-400 mt-1">{sub}</p>
      {cta && <button onClick={onCta} className="mt-3 text-primary-custom text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all" style={{ color: "#E11D2E" }}>{cta}<Icon name="arrow_forward" className="text-sm" /></button>}
    </div>
  );
};

const DashboardTab = ({ role, goTo, refreshHeader }) => {
  const [d, setD] = useState(null);
  const [groups, setGroups] = useState([]);
  const [members, setMembers] = useState([]);
  const [service, setService] = useState("airtime");
  const [amount, setAmount] = useState(1000);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [groupTopup, setGroupTopup] = useState(null);

  const load = useCallback(() => {
    entApi.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    entApi.get("/departments").then((r) => setGroups(r.data.items || [])).catch(() => {});
    entApi.get("/members").then((r) => setMembers(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!d) return <Loader />;

  const memberCount = members.length || d.members || 0;
  const effAmount = custom ? Number(custom) : amount;
  const canTopup = can(role, "manager");

  const topUpEveryone = async () => {
    if (service === "data") { goTo("data"); return; }
    if (!effAmount || effAmount < 50) { toast("Enter a valid amount (min ₦50)", false); return; }
    if (memberCount === 0) { toast("Add members first", false); return; }
    if (!window.confirm(`Send ₦${effAmount.toLocaleString()} airtime to all ${memberCount} members?`)) return;
    setBusy(true);
    try {
      const items = members.map((m) => ({ member_id: m.id, amount: effAmount }));
      const r = await entApi.post("/topup", { service_type: "airtime", items });
      const ok = r.data?.summary?.successful ?? 0;
      toast(`Sent to ${ok}/${items.length} members 🎉`);
      load(); refreshHeader && refreshHeader();
    } catch (e) {
      toast(e.response?.data?.detail || "Top-up failed", false);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="ent-dashboard">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2"><Icon name="business_center" className="text-red-600" />Business Mode</h1>
        <p className="text-slate-500 text-sm">Top up everyone. One click.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <BizStat testid="ent-stat-balance" icon="account_balance_wallet" tone="red" label="Organisation Balance" value={naira(d.wallet_balance)} sub="Fund your organisation wallet"
          cta={canTopup ? "Fund Wallet" : null} onCta={() => goTo("wallet")} />
        <BizStat testid="ent-stat-members" icon="groups" tone="violet" label="Total Members" value={memberCount} sub="All across groups"
          cta="View Members" onCta={() => goTo("members")} />
        <BizStat testid="ent-stat-month-spend" icon="insights" tone="green" label="This Month's Spend" value={naira(d.this_month_spend || 0)}
          sub={d.spend_trend_pct != null ? `${Math.abs(d.spend_trend_pct)}% ${d.spend_trend_pct <= 0 ? "less" : "more"} than last month` : "So far this month"}
          cta="View Reports" onCta={() => goTo("analytics")} />
        <BizStat testid="ent-stat-success" icon="task_alt" tone="amber" label="Successful Top-Ups" value={d.this_month_topups || 0} sub="This month"
          cta="View Activity" onCta={() => goTo("transactions")} />
      </div>

      {/* Three columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Up Everyone */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="ent-topup-everyone">
          <h3 className="font-bold text-slate-900">Top Up Everyone</h3>
          <p className="text-xs text-slate-400 mb-4">Send airtime or data to all members in one click.</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button onClick={() => setService("airtime")} data-testid="ent-everyone-airtime"
              className={`flex items-center gap-2 p-3 rounded-xl border text-left ${service === "airtime" ? "border-red-500 bg-red-50" : "border-slate-200"}`}>
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${service === "airtime" ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-500"}`}><Icon name="smartphone" /></span>
              <div><p className="font-bold text-sm text-slate-900">Airtime</p><p className="text-[11px] text-slate-400">Send to everyone</p></div>
            </button>
            <button onClick={() => setService("data")} data-testid="ent-everyone-data"
              className={`flex items-center gap-2 p-3 rounded-xl border text-left ${service === "data" ? "border-red-500 bg-red-50" : "border-slate-200"}`}>
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${service === "data" ? "bg-red-100 text-red-600" : "bg-blue-100 text-blue-600"}`}><Icon name="wifi" /></span>
              <div><p className="font-bold text-sm text-slate-900">Data</p><p className="text-[11px] text-slate-400">Send data to all</p></div>
            </button>
          </div>

          {service === "airtime" ? (
            <>
              <p className="text-xs font-semibold text-slate-500 mb-2">Select Amount</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {AMOUNT_PRESETS.map((a) => (
                  <button key={a} onClick={() => { setAmount(a); setCustom(""); }} data-testid={`ent-amount-${a}`}
                    className={`px-3 py-2 rounded-lg text-sm font-bold border ${!custom && amount === a ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-600 border-slate-200"}`}>{naira(a)}</button>
                ))}
              </div>
              <input value={custom} onChange={(e) => setCustom(e.target.value.replace(/\D/g, ""))} data-testid="ent-amount-custom"
                placeholder="Or enter custom amount (₦)" inputMode="numeric"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-500 mb-4" />
              <button onClick={topUpEveryone} disabled={busy || !canTopup} data-testid="ent-everyone-submit"
                className="w-full bg-gradient-to-r from-red-600 to-red-500 text-white font-bold py-3.5 rounded-xl disabled:opacity-60 flex items-center justify-center gap-2">
                <Icon name="bolt" />{busy ? "Sending…" : "Top Up Everyone Now"}</button>
              <p className="text-center text-xs text-slate-400 mt-2">
                {canTopup ? `This will send ${naira(effAmount || 0)} airtime to ${memberCount} members` : "Viewers cannot send top-ups"}</p>
            </>
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-slate-500 mb-4">Data top-ups use a plan per network. Pick networks & plans on the Data page, then select all members.</p>
              <button onClick={() => goTo("data")} data-testid="ent-everyone-data-go" className="w-full bg-gradient-to-r from-red-600 to-red-500 text-white font-bold py-3.5 rounded-xl">Choose data plans →</button>
            </div>
          )}
        </div>

        {/* Your Groups */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="ent-groups">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-slate-900">Your Groups</h3>
            <button onClick={() => goTo("members")} className="text-red-600 text-xs font-semibold">View all →</button>
          </div>
          <p className="text-xs text-slate-400 mb-3">Manage groups and members</p>
          <div className="space-y-2">
            {groups.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No groups yet. Create your first group.</p>}
            {groups.slice(0, 6).map((g, i) => {
              const t = GROUP_TONES[i % GROUP_TONES.length];
              return (
                <div key={g.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50" data-testid={`ent-group-${g.id}`}>
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${t.bg} ${t.ic}`}><Icon name="folder_shared" className="text-lg" /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">{g.name}</p>
                    <p className="text-xs text-slate-400">{g.member_count} members</p>
                  </div>
                  {canTopup && g.member_count > 0 && (
                    <button onClick={() => setGroupTopup(g)} data-testid={`ent-group-topup-${g.id}`}
                      className="text-xs font-bold text-red-600 border border-red-200 hover:bg-red-50 px-2.5 py-1.5 rounded-lg whitespace-nowrap">Top up</button>
                  )}
                  <button onClick={() => goTo("members")} className="text-slate-300 hover:text-slate-500"><Icon name="more_horiz" /></button>
                </div>
              );
            })}
          </div>
          <button onClick={() => goTo("members")} data-testid="ent-create-group"
            className="mt-3 w-full border-2 border-dashed border-red-200 text-red-600 font-semibold py-2.5 rounded-xl text-sm hover:bg-red-50">+ Create New Group</button>
        </div>

        {/* Recent Top-Ups */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="ent-recent-list">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-slate-900">Recent Top-Ups</h3>
            <button onClick={() => goTo("transactions")} className="text-red-600 text-xs font-semibold">View all →</button>
          </div>
          <div className="space-y-1">
            {d.recent.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No top-ups yet.</p>}
            {d.recent.map((t) => (
              <div key={t.id} className="flex items-center gap-3 py-2">
                <NetworkLogoAvatar provider={t.provider} serviceType={t.service_type} size={36} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">{t.member_name}</p>
                  <p className="text-xs text-slate-400 truncate">{t.phone} · {fmtDate(t.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-900 text-sm">{naira(t.face_amount)}</p>
                  <p className="text-[11px] text-slate-400 capitalize">{t.service_type}</p>
                </div>
                <Icon name={t.status === "success" ? "check_circle" : t.status === "pending" ? "schedule" : "error"}
                  className={`text-lg ${t.status === "success" ? "text-emerald-500" : t.status === "pending" ? "text-amber-500" : "text-red-500"}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center justify-between gap-4" data-testid="ent-schedule-card">
          <div>
            <h3 className="font-bold text-slate-900 flex items-center gap-2"><Icon name="event_repeat" className="text-red-600" />Schedule Automatic Top-Ups</h3>
            <p className="text-xs text-slate-400 mb-3">Set up recurring top-ups for your groups</p>
            <ul className="space-y-1.5 mb-4">
              {["Choose amount and frequency", "Never miss a monthly top-up", "Members get it automatically"].map((x) => (
                <li key={x} className="flex items-center gap-2 text-sm text-slate-600"><Icon name="check_circle" className="text-emerald-500 text-base" />{x}</li>
              ))}
            </ul>
            <button onClick={() => goTo("schedules")} data-testid="ent-create-schedule" className="bg-red-600 hover:bg-red-700 text-white font-bold text-sm px-5 py-2.5 rounded-xl">+ Create Schedule</button>
          </div>
          <Icon name="calendar_month" className="text-6xl text-red-100 hidden sm:block" />
        </div>

        <div className="relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white flex items-center justify-between gap-4" data-testid="ent-biz-simple">
          <div className="relative z-10">
            <h3 className="font-extrabold text-lg leading-tight">Business made simple</h3>
            <p className="text-sm text-white/85 mt-1 max-w-xs">Top up airtime or data for your whole team, group, or organisation in seconds.</p>
            <button onClick={() => goTo("analytics")} className="mt-4 inline-flex items-center gap-1.5 bg-white text-violet-700 font-bold text-sm px-4 py-2 rounded-xl">Learn more<Icon name="arrow_forward" className="text-base" /></button>
          </div>
          <span className="text-6xl relative z-10">🏢</span>
          <div className="absolute -right-6 -bottom-8 w-40 h-40 bg-white/15 rounded-full blur-2xl" />
        </div>
      </div>

      {groupTopup && (
        <GroupTopUpModal group={groupTopup} members={members}
          onClose={() => setGroupTopup(null)}
          onDone={() => { setGroupTopup(null); load(); refreshHeader && refreshHeader(); }} />
      )}
    </div>
  );
};

// ==================== MEMBER PICKER (for top-up) ====================
const MemberPicker = ({ members, departments, selected, onToggle, onToggleDept, networkFilter }) => {
  const [q, setQ] = useState("");
  const byDept = useMemo(() => {
    const map = {};
    members
      .filter((m) => !networkFilter || m.provider === networkFilter)
      .filter((m) => !q || m.name.toLowerCase().includes(q.toLowerCase()) || m.phone.includes(q))
      .forEach((m) => {
        const key = m.department_id || "_none";
        (map[key] = map[key] || []).push(m);
      });
    return map;
  }, [members, q, networkFilter]);
  const deptName = (id) => (id === "_none" ? "Unassigned" : (departments.find((d) => d.id === id)?.name || "Department"));

  return (
    <div className="bg-white rounded-2xl border border-slate-200">
      <div className="p-3 border-b border-slate-100">
        <div className="relative">
          <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
          <input data-testid="ent-member-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members..." className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-red-500 text-sm" />
        </div>
      </div>
      <div className="max-h-[380px] overflow-y-auto divide-y divide-slate-50" data-testid="ent-member-picker">
        {Object.keys(byDept).length === 0 && <div className="px-5 py-10 text-center text-slate-400 text-sm">No members{networkFilter ? ` on ${networkFilter}` : ""}. Add members in the Departments tab.</div>}
        {Object.entries(byDept).map(([did, list]) => {
          const ids = list.map((m) => m.id);
          const allSel = ids.every((id) => selected.includes(id));
          return (
            <div key={did}>
              <div className="flex items-center justify-between px-4 py-2 bg-slate-50 sticky top-0">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">{deptName(did)} ({list.length})</span>
                <button onClick={() => onToggleDept(ids, !allSel)} data-testid={`ent-dept-selectall-${did}`} className="text-xs text-red-600 font-semibold">{allSel ? "Clear" : "Select all"}</button>
              </div>
              {list.map((m) => (
                <label key={m.id} data-testid={`ent-member-row-${m.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={selected.includes(m.id)} onChange={() => onToggle(m.id)} data-testid={`ent-member-check-${m.id}`} className="w-4 h-4 accent-red-600" />
                  <NetworkLogoAvatar provider={m.provider} size={34} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{m.name}</p>
                    <p className="text-xs text-slate-400">{m.phone}</p>
                  </div>
                </label>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== TOP-UP (airtime + data) ====================
const PRESETS = [100, 200, 500, 1000, 2000];

const TopUpTab = ({ service, role, refreshHeader }) => {
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selected, setSelected] = useState([]);
  const [amount, setAmount] = useState(service === "airtime" ? 200 : 0);
  const [network, setNetwork] = useState("MTN"); // data: filters members + plans
  const [plans, setPlans] = useState({});
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const load = useCallback(() => {
    entApi.get("/members").then((r) => setMembers(r.data.items)).catch(() => {});
    entApi.get("/departments").then((r) => setDepartments(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (service === "data") {
      axios.get(`${API}/subscriptions`).then((r) => setPlans(r.data.providers || {})).catch(() => {});
    }
  }, [service]);

  const networkFilter = service === "data" ? network : null;
  const visibleSelected = selected.filter((id) => {
    const m = members.find((x) => x.id === id);
    return m && (!networkFilter || m.provider === networkFilter);
  });

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleDept = (ids, add) => setSelected((s) => add ? [...new Set([...s, ...ids])] : s.filter((x) => !ids.includes(x)));

  const dataPlans = service === "data" ? (plans[network]?.data?.plans || []) : [];
  const effAmount = service === "data" ? (plan?.amount || 0) : amount;

  const submit = async () => {
    if (service === "data" && !plan) return toast("Select a data plan", false);
    if (service === "airtime" && (!amount || amount <= 0)) return toast("Enter an amount", false);
    if (visibleSelected.length === 0) return toast("Select at least one member", false);
    setBusy(true); setResults(null);
    try {
      const items = visibleSelected.map((member_id) => ({
        member_id,
        amount: effAmount,
        ...(service === "data" ? { subcode: plan.subCode } : {}),
      }));
      const r = await entApi.post("/topup", { service_type: service, items });
      setResults(r.data);
      setSelected([]);
      refreshHeader && refreshHeader();
      toast(`Topped up ${r.data.summary.success}/${r.data.summary.total} member(s)`);
    } catch (err) {
      toast(err.response?.data?.detail || "Top-up failed", false);
    } finally { setBusy(false); }
  };

  if (!can(role, "manager")) {
    return <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500" data-testid="ent-topup-readonly">
      <Icon name="lock" className="text-4xl text-slate-300 mb-2" /><p>Your role (Viewer) does not have permission to perform top-ups.</p>
    </div>;
  }

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid={`ent-topup-${service}`}>
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-3">{service === "airtime" ? "Airtime amount" : "Choose network & plan"}</h3>
          {service === "data" && (
            <div className="flex flex-wrap gap-2 mb-4">
              {PROVIDERS.map((p) => (
                <button key={p.name} onClick={() => { setNetwork(p.name); setPlan(null); }} data-testid={`ent-network-${p.name}`}
                  className={`px-3 py-2 rounded-xl text-sm font-semibold border transition-colors ${network === p.name ? "border-red-500 bg-red-50 text-red-700" : "border-slate-200 text-slate-600"}`}>
                  {p.name}
                </button>
              ))}
            </div>
          )}
          {service === "airtime" ? (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {PRESETS.map((a) => (
                  <button key={a} onClick={() => setAmount(a)} data-testid={`ent-preset-${a}`}
                    className={`py-2.5 rounded-xl text-sm font-bold border ${amount === a ? "border-red-500 bg-red-50 text-red-700" : "border-slate-200 text-slate-700"}`}>₦{a}</button>
                ))}
              </div>
              <input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} data-testid="ent-amount" placeholder="Custom amount" className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
            </>
          ) : (
            <div className="max-h-[280px] overflow-y-auto space-y-2" data-testid="ent-data-plans">
              {dataPlans.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Loading plans…</p>}
              {dataPlans.map((pl, i) => (
                <button key={`${pl.subCode}-${i}`} onClick={() => setPlan(pl)} data-testid={`ent-plan-${pl.subCode}`}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left ${plan?.subCode === pl.subCode && plan?.amount === pl.amount ? "border-red-500 bg-red-50" : "border-slate-200"}`}>
                  <span className="text-sm text-slate-700">{pl.description}</span>
                  <span className="font-bold text-red-600 text-sm">{naira(pl.amount)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-slate-900 rounded-2xl p-5 text-white" data-testid="ent-topup-summary">
          <div className="flex justify-between text-sm text-slate-300"><span>Recipients</span><span>{visibleSelected.length}</span></div>
          <div className="flex justify-between text-sm text-slate-300 mt-1"><span>Face value each</span><span>{naira(effAmount)}</span></div>
          <div className="flex justify-between text-sm text-slate-300 mt-1"><span>Total face value</span><span>{naira(effAmount * visibleSelected.length)}</span></div>
          <div className="border-t border-white/10 mt-3 pt-3 flex justify-between font-bold">
            <span>Discount applied at checkout</span><span className="text-green-400">−{service === "airtime" ? "airtime" : "data"} %</span>
          </div>
          <button onClick={submit} disabled={busy} data-testid="ent-topup-submit"
            className="w-full mt-4 bg-red-600 hover:bg-red-700 rounded-xl py-3 font-bold disabled:opacity-50">
            {busy ? "Processing…" : `Top up ${visibleSelected.length} member(s)`}
          </button>
        </div>

        {results && (
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="ent-topup-results">
            <h3 className="font-bold text-slate-900 mb-3">Results · charged {naira(results.summary.charged)}</h3>
            <div className="space-y-2">
              {results.results.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{r.name} · {r.phone}</span>
                  <div className="flex items-center gap-2"><span className="text-slate-400">{naira(r.charge)}</span><StatusBadge status={r.status} /></div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-slate-900">Select members{service === "data" ? ` · ${network}` : ""}</h3>
          <span className="text-sm text-slate-400">{visibleSelected.length} selected</span>
        </div>
        <MemberPicker members={members} departments={departments} selected={selected} onToggle={toggle} onToggleDept={toggleDept} networkFilter={networkFilter} />
      </div>
    </div>
  );
};

// ==================== MEMBERS / DEPARTMENTS ====================
const MembersTab = ({ role }) => {
  const [departments, setDepartments] = useState([]);
  const [members, setMembers] = useState([]);
  const [filterDept, setFilterDept] = useState("");
  const [search, setSearch] = useState("");
  const [deptModal, setDeptModal] = useState(null); // {} new or {id,...}
  const [memberModal, setMemberModal] = useState(null);
  const [history, setHistory] = useState(null);
  const editable = can(role, "manager");

  const load = useCallback(() => {
    entApi.get("/departments").then((r) => setDepartments(r.data.items)).catch(() => {});
    const params = new URLSearchParams();
    if (filterDept) params.set("department_id", filterDept);
    if (search.trim()) params.set("search", search.trim());
    const qs = params.toString();
    entApi.get(`/members${qs ? `?${qs}` : ""}`).then((r) => setMembers(r.data.items)).catch(() => {});
  }, [filterDept, search]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const saveDept = async (body) => {
    try {
      if (deptModal.id) await entApi.patch(`/departments/${deptModal.id}`, body);
      else await entApi.post("/departments", body);
      setDeptModal(null); load(); toast("Department saved");
    } catch (e) { toast(e.response?.data?.detail || "Failed", false); }
  };
  const delDept = async (id) => {
    if (!window.confirm("Delete this department? Members will be unassigned.")) return;
    await entApi.delete(`/departments/${id}`); load(); toast("Department deleted");
  };
  const saveMember = async (body) => {
    try {
      if (memberModal.id) await entApi.patch(`/members/${memberModal.id}`, body);
      else await entApi.post("/members", body);
      setMemberModal(null); load(); toast("Member saved");
    } catch (e) { toast(e.response?.data?.detail || "Failed", false); }
  };
  const delMember = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    await entApi.delete(`/members/${id}`); load(); toast("Member removed");
  };
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    let start = 0;
    let idx = { name: 0, phone: 1, department: 2 };
    if (lines[0].toLowerCase().includes("phone") || lines[0].toLowerCase().includes("name")) {
      const cols = lines[0].split(",").map((c) => c.replace(/^"|"$/g, "").trim().toLowerCase());
      idx = { name: cols.indexOf("name"), phone: cols.indexOf("phone"), department: cols.indexOf("department") };
      if (idx.name < 0) idx.name = 0;
      if (idx.phone < 0) idx.phone = 1;
      start = 1;
    }
    const rows = [];
    for (let i = start; i < lines.length; i++) {
      const c = lines[i].split(",").map((x) => x.replace(/^"|"$/g, "").trim());
      rows.push({ name: c[idx.name] || "", phone: c[idx.phone] || "", department: idx.department >= 0 ? (c[idx.department] || "") : "" });
    }
    return rows;
  };
  const importCSV = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const rows = parseCSV(await file.text());
      if (!rows.length) { toast("No rows found in file", false); return; }
      const r = await entApi.post("/members/import", { rows });
      const d = r.data;
      toast(`Imported ${d.added} member(s)${d.skipped ? `, ${d.skipped} skipped` : ""}${d.errors?.length ? `, ${d.errors.length} error(s)` : ""}`);
      load();
    } catch (e) { toast(e.response?.data?.detail || "Import failed", false); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const openHistory = async (m) => {
    const r = await entApi.get(`/members/${m.id}/history`);
    setHistory(r.data);
  };

  return (
    <div className="space-y-6" data-testid="ent-members">
      {/* Departments */}
      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Departments</h3>
          {editable && <button onClick={() => setDeptModal({})} data-testid="ent-add-dept" className="bg-red-600 text-white px-3 py-2 rounded-xl text-sm font-semibold flex items-center gap-1.5"><Icon name="add" className="text-base" /> Department</button>}
        </div>
        <div className="p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ent-dept-list">
          {departments.length === 0 && <p className="text-slate-400 text-sm col-span-full py-6 text-center">No departments yet.</p>}
          {departments.map((d) => (
            <div key={d.id} data-testid={`ent-dept-${d.id}`} className="border border-slate-200 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-bold text-slate-900">{d.name}</p>
                  <p className="text-xs text-slate-400">{d.member_count} member(s)</p>
                </div>
                {editable && (
                  <div className="flex gap-1">
                    <button onClick={() => setDeptModal(d)} data-testid={`ent-dept-edit-${d.id}`} className="text-slate-400 hover:text-slate-700"><Icon name="edit" className="text-lg" /></button>
                    <button onClick={() => delDept(d.id)} data-testid={`ent-dept-del-${d.id}`} className="text-slate-400 hover:text-red-600"><Icon name="delete" className="text-lg" /></button>
                  </div>
                )}
              </div>
              {d.description && <p className="text-sm text-slate-500 mt-2">{d.description}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Members */}
      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-slate-900">Members</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="ent-members-search" placeholder="Search name or phone..." className="pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm outline-none focus:border-red-500 w-48" />
            </div>
            <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)} data-testid="ent-member-filter" className="border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none">
              <option value="">All departments</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {editable && <>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="ent-import-file" onChange={(e) => importCSV(e.target.files?.[0])} />
              <button onClick={() => downloadCSV("topit-members-template.csv", [["name", "phone", "department"], ["Ada Obi", "08031234567", "Sales"]])} data-testid="ent-import-template" className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold hover:bg-slate-50">Template</button>
              <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="ent-import-csv" className="border border-slate-200 text-slate-700 px-3 py-2 rounded-xl text-sm font-semibold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50"><Icon name="upload_file" className="text-base" /> {importing ? "Importing…" : "Import CSV"}</button>
              <button onClick={() => setMemberModal({})} data-testid="ent-add-member" className="bg-red-600 text-white px-3 py-2 rounded-xl text-sm font-semibold flex items-center gap-1.5"><Icon name="person_add" className="text-base" /> Member</button>
            </>}
          </div>
        </div>
        <div className="divide-y divide-slate-100" data-testid="ent-member-list">
          {members.length === 0 && <p className="px-5 py-10 text-center text-slate-400">{search ? "No members match your search." : "No members."}</p>}
          {members.map((m) => (
            <div key={m.id} data-testid={`ent-member-${m.id}`} className="px-5 py-3 flex items-center gap-3">
              <NetworkLogoAvatar provider={m.provider} size={38} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 text-sm truncate">{m.name}</p>
                <p className="text-xs text-slate-400">{m.phone} · {m.department_name || "Unassigned"}</p>
              </div>
              <button onClick={() => openHistory(m)} data-testid={`ent-member-history-${m.id}`} className="text-slate-400 hover:text-slate-700" title="History"><Icon name="history" /></button>
              {editable && <>
                <button onClick={() => setMemberModal(m)} data-testid={`ent-member-edit-${m.id}`} className="text-slate-400 hover:text-slate-700"><Icon name="edit" /></button>
                <button onClick={() => delMember(m.id)} data-testid={`ent-member-del-${m.id}`} className="text-slate-400 hover:text-red-600"><Icon name="delete" /></button>
              </>}
            </div>
          ))}
        </div>
      </div>

      {deptModal && <DeptModal dept={deptModal} onSave={saveDept} onClose={() => setDeptModal(null)} />}
      {memberModal && <MemberModal member={memberModal} departments={departments} onSave={saveMember} onClose={() => setMemberModal(null)} />}
      {history && <HistoryModal data={history} onClose={() => setHistory(null)} />}
    </div>
  );
};

const DeptModal = ({ dept, onSave, onClose }) => {
  const [f, setF] = useState({ name: dept.name || "", description: dept.description || "" });
  return (
    <Modal title={dept.id ? "Edit Department" : "New Department"} onClose={onClose} testid="ent-dept-modal">
      <div className="space-y-3">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ent-dept-name" placeholder="Department name" className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
        <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ent-dept-desc" placeholder="Description (optional)" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-red-500" rows={2} />
        <button onClick={() => onSave(f)} data-testid="ent-dept-save" className="w-full h-11 bg-red-600 text-white rounded-xl font-bold">Save</button>
      </div>
    </Modal>
  );
};

const MemberModal = ({ member, departments, onSave, onClose }) => {
  const [f, setF] = useState({ name: member.name || "", phone: member.phone || "", provider: member.provider || "", department_id: member.department_id || "" });
  return (
    <Modal title={member.id ? "Edit Member" : "Add Member"} onClose={onClose} testid="ent-member-modal">
      <div className="space-y-3">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ent-member-name" placeholder="Full name" className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
        <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="ent-member-phone" placeholder="Phone (e.g. 0803...)" className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
        <select value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} data-testid="ent-member-provider" className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none">
          <option value="">Auto-detect network</option>
          {PROVIDERS.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
        </select>
        <select value={f.department_id} onChange={(e) => setF({ ...f, department_id: e.target.value })} data-testid="ent-member-dept" className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none">
          <option value="">No department</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button onClick={() => onSave(f)} data-testid="ent-member-save" className="w-full h-11 bg-red-600 text-white rounded-xl font-bold">Save</button>
      </div>
    </Modal>
  );
};

const HistoryModal = ({ data, onClose }) => (
  <Modal title={`${data.member.name} · History`} onClose={onClose} testid="ent-history-modal">
    <p className="text-sm text-slate-500 mb-3">{data.total} top-up(s) · {naira(data.total_spent)} charged</p>
    <div className="space-y-2 max-h-[50vh] overflow-y-auto">
      {data.items.length === 0 && <p className="text-slate-400 text-sm py-6 text-center">No top-ups for this member yet.</p>}
      {data.items.map((t) => (
        <div key={t.id} className="flex items-center justify-between border border-slate-100 rounded-xl px-3 py-2.5">
          <div className="flex items-center gap-2"><NetworkLogoAvatar provider={t.provider} serviceType={t.service_type} size={32} />
            <div><p className="text-sm font-semibold text-slate-800">{naira(t.face_amount)} {t.service_type}</p><p className="text-xs text-slate-400">{fmtDate(t.created_at)}</p></div>
          </div>
          <StatusBadge status={t.status} />
        </div>
      ))}
    </div>
  </Modal>
);

// ==================== TRANSACTIONS ====================
const TransactionsTab = () => {
  const [data, setData] = useState(null);
  const [svc, setSvc] = useState("");
  const [page, setPage] = useState(1);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const filterQS = `${svc ? `&service_type=${svc}` : ""}${from ? `&date_from=${from}` : ""}${to ? `&date_to=${to}` : ""}`;
  useEffect(() => { entApi.get(`/transactions?page=${page}&limit=20${filterQS}`).then((r) => setData(r.data)).catch(() => {}); }, [page, svc, from, to]);

  const buildRows = async () => {
    const r = await entApi.get(`/transactions?page=1&limit=100000${filterQS}`);
    const header = ["Date", "Recipient", "Phone", "Network", "Service", "Face Amount", "Wallet Charged", "Discount %", "Performed By", "Status", "Reference"];
    const body = r.data.items.map((t) => [
      t.created_at ? new Date(t.created_at).toLocaleString() : "", t.member_name, t.phone, t.provider,
      t.service_type, t.face_amount, t.wallet_charged, t.discount_pct ?? "", t.performed_by_name || "",
      t.status, t.transaction_reference || "",
    ]);
    return [header, ...body];
  };
  const exportFile = async (kind) => {
    setExporting(true);
    try {
      const rows = await buildRows();
      const stamp = Date.now();
      if (kind === "csv") downloadCSV(`topit-enterprise-transactions-${stamp}.csv`, rows);
      else await downloadXLSX(`topit-enterprise-transactions-${stamp}.xlsx`, "Transactions", rows);
      toast(`Exported ${rows.length - 1} transaction(s)`);
    } catch { toast("Export failed", false); } finally { setExporting(false); }
  };

  const downloadReceipt = async (t) => {
    try {
      const { default: jsPDF } = await import("jspdf");
      const doc = new jsPDF({ unit: "pt", format: "a5" });
      const W = doc.internal.pageSize.getWidth();
      doc.setFillColor(11, 18, 32); doc.rect(0, 0, W, 72, "F");
      doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(20);
      doc.text("TopIt", 40, 44);
      doc.setFontSize(10); doc.setFont("helvetica", "normal");
      doc.text("Payment Receipt", W - 40, 44, { align: "right" });
      let y = 110;
      doc.setTextColor(17, 24, 39); doc.setFont("helvetica", "bold"); doc.setFontSize(22);
      doc.text(naira(t.face_amount), 40, y);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(120, 120, 120);
      y += 18; doc.text(`${(t.service_type || "").toUpperCase()} · ${t.provider || ""}`, 40, y);
      y += 28;
      const rows = [
        ["Recipient", t.member_name || "—"],
        ["Phone", t.phone || "—"],
        ["Face amount", naira(t.face_amount)],
        ["Wallet charged", naira(t.wallet_charged)],
        t.discount_pct ? ["Discount", `${t.discount_pct}%`] : null,
        ["Status", (t.status || "").toUpperCase()],
        ["Performed by", t.performed_by_name || "—"],
        ["Date", t.created_at ? new Date(t.created_at).toLocaleString() : "—"],
        ["Reference", t.transaction_reference || "—"],
      ].filter(Boolean);
      doc.setFontSize(11);
      rows.forEach(([k, v]) => {
        doc.setTextColor(120, 120, 120); doc.text(String(k), 40, y);
        doc.setTextColor(17, 24, 39); doc.text(String(v), W - 40, y, { align: "right" });
        y += 22;
      });
      y += 8; doc.setDrawColor(230, 230, 230); doc.line(40, y, W - 40, y); y += 20;
      doc.setTextColor(150, 150, 150); doc.setFontSize(9);
      doc.text("Thank you for using TopIt.", 40, y);
      doc.save(`topit-receipt-${t.transaction_reference || t.id}.pdf`);
    } catch { toast("Could not generate receipt", false); }
  };

  return (
    <div className="space-y-4" data-testid="ent-transactions">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          {["", "airtime", "data"].map((s) => (
            <button key={s} onClick={() => { setSvc(s); setPage(1); }} data-testid={`ent-tx-filter-${s || "all"}`}
              className={`px-4 py-2 rounded-xl text-sm font-semibold ${svc === s ? "bg-red-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
              {s ? s[0].toUpperCase() + s.slice(1) : "All"}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportFile("csv")} disabled={exporting} data-testid="ent-tx-export-csv" className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50"><Icon name="table_view" className="text-base" /> CSV</button>
          <button onClick={() => exportFile("xlsx")} disabled={exporting} data-testid="ent-tx-export-xlsx" className="px-3 py-2 rounded-xl bg-green-700 text-white text-sm font-semibold flex items-center gap-1.5 hover:bg-green-800 disabled:opacity-50"><Icon name="grid_on" className="text-base" /> Excel</button>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap bg-white rounded-xl border border-slate-200 px-4 py-2.5">
        <Icon name="date_range" className="text-slate-400" />
        <label className="text-sm text-slate-500">From</label>
        <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} data-testid="ent-tx-date-from" className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-red-500" />
        <label className="text-sm text-slate-500">To</label>
        <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} data-testid="ent-tx-date-to" className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-red-500" />
        {(from || to) && <button onClick={() => { setFrom(""); setTo(""); setPage(1); }} data-testid="ent-tx-date-clear" className="text-sm text-red-600 font-semibold ml-1">Clear</button>}
        {data && <span className="text-xs text-slate-400 ml-auto">{data.total} result(s)</span>}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr><th className="px-5 py-3 text-left">Recipient</th><th className="px-5 py-3 text-left">Service</th><th className="px-5 py-3 text-right">Face</th><th className="px-5 py-3 text-right">Charged</th><th className="px-5 py-3 text-left">By</th><th className="px-5 py-3 text-left">When</th><th className="px-5 py-3 text-left">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="ent-tx-rows">
            {!data && <tr><td colSpan="7" className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>}
            {data && data.items.length === 0 && <tr><td colSpan="7" className="px-5 py-10 text-center text-slate-400">No transactions yet.</td></tr>}
            {data && data.items.map((t) => (
              <tr key={t.id}>
                <td className="px-5 py-3"><div className="flex items-center gap-2"><NetworkLogoAvatar provider={t.provider} size={30} /><div><p className="font-semibold text-slate-800">{t.member_name}</p><p className="text-xs text-slate-400">{t.phone}</p></div></div></td>
                <td className="px-5 py-3 capitalize text-slate-600">{t.service_type}</td>
                <td className="px-5 py-3 text-right font-semibold">{naira(t.face_amount)}</td>
                <td className="px-5 py-3 text-right text-slate-500">{naira(t.wallet_charged)}</td>
                <td className="px-5 py-3 text-slate-500">{t.performed_by_name || "—"}</td>
                <td className="px-5 py-3 text-slate-500">{fmtDate(t.created_at)}</td>
                <td className="px-5 py-3"><div className="flex items-center gap-2"><StatusBadge status={t.status} /><button onClick={() => downloadReceipt(t)} data-testid={`ent-tx-receipt-${t.id}`} title="Download receipt" className="text-slate-400 hover:text-red-600"><Icon name="receipt_long" className="text-base" /></button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="text-sm text-slate-600 disabled:opacity-40">Previous</button>
            <span className="text-xs text-slate-400">Page {data.page} of {data.pages}</span>
            <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="text-sm text-slate-600 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ANALYTICS ====================
const AnalyticsTab = ({ entName }) => {
  const [d, setD] = useState(null);
  useEffect(() => { entApi.get("/analytics").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Loader />;
  const maxMonth = Math.max(...d.monthly.map((m) => m.spent), 1);
  const exportCSV = () => {
    const rows = [["TopIt Enterprise Analytics", entName], [], ["Summary"], ["Total Spent", d.summary.total_spent], ["Total Saved", d.summary.total_saved], ["Total Top-Ups", d.summary.total_topups], [], ["By Department", "Spent"], ...d.by_department.map((x) => [x.name, x.spent]), [], ["Top Members", "Phone", "Department", "Top-Ups", "Spent"], ...d.top_members.map((m) => [m.name, m.phone, m.department, m.topups, m.spent])];
    downloadCSV(`topit-enterprise-analytics-${Date.now()}.csv`, rows);
  };
  return (
    <div className="space-y-6" data-testid="ent-analytics">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-900">Spend insights</h3>
        <div className="flex gap-2">
          <button onClick={exportCSV} data-testid="ent-export-csv" className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold flex items-center gap-1.5 hover:bg-slate-50"><Icon name="table_view" className="text-base" /> CSV</button>
          <button onClick={() => exportAnalyticsPDF(entName, d).catch(() => toast("Export failed", false))} data-testid="ent-export-pdf" className="px-3 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold flex items-center gap-1.5 hover:bg-slate-800"><Icon name="picture_as_pdf" className="text-base" /> PDF</button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon="payments" label="Total Spent" value={naira(d.summary.total_spent)} tone="red" testid="ent-an-spent" />
        <StatCard icon="savings" label="Saved via Discounts" value={naira(d.summary.total_saved)} tone="green" testid="ent-an-saved" />
        <StatCard icon="bolt" label="Total Top-Ups" value={d.summary.total_topups} tone="blue" testid="ent-an-topups" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-4">Monthly spend (last 6 months)</h3>
          <div className="flex items-end gap-3 h-40">
            {d.monthly.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full bg-red-500 rounded-t-lg" style={{ height: `${(m.spent / maxMonth) * 100}%`, minHeight: m.spent ? 4 : 0 }} title={naira(m.spent)} />
                <span className="text-xs text-slate-400">{m.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-4">By network</h3>
          <div className="flex items-center gap-6">
            <NetworkDonut segments={d.by_provider} total={d.summary.total_spent} />
            <div className="space-y-2 flex-1">
              {d.by_provider.length === 0 && <p className="text-slate-400 text-sm">No data yet.</p>}
              {d.by_provider.map((p) => (
                <div key={p.network} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: providerMeta(p.network).color }} />{p.network}</span>
                  <span className="font-semibold">{naira(p.spent)} <span className="text-slate-400">({p.pct}%)</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-3">By service</h3>
          {d.by_service.map((s) => (
            <div key={s.service} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <span className="capitalize text-slate-700">{s.service}</span>
              <span className="text-sm text-slate-500">{s.topups} top-ups · <span className="font-semibold text-slate-800">{naira(s.spent)}</span></span>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-3">By department</h3>
          {d.by_department.length === 0 && <p className="text-slate-400 text-sm">No data yet.</p>}
          {d.by_department.map((s) => (
            <div key={s.name} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <span className="text-slate-700">{s.name}</span>
              <span className="font-semibold text-slate-800">{naira(s.spent)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-bold text-slate-900">Top members leaderboard</h3></div>
        <div className="divide-y divide-slate-100" data-testid="ent-leaderboard">
          {d.top_members.length === 0 && <p className="px-5 py-10 text-center text-slate-400">No top-ups yet.</p>}
          {d.top_members.map((m, i) => (
            <div key={m.id} className="px-5 py-3 flex items-center gap-3">
              <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold ${i < 3 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
              <NetworkLogoAvatar provider={m.provider} size={36} />
              <div className="flex-1 min-w-0"><p className="font-semibold text-slate-900 text-sm truncate">{m.name}</p><p className="text-xs text-slate-400">{m.phone} · {m.department}</p></div>
              <div className="text-right"><p className="font-bold text-slate-900 text-sm">{naira(m.spent)}</p><p className="text-xs text-slate-400">{m.topups} top-ups</p></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== WALLET ====================
const WalletTab = ({ role, refreshHeader }) => {
  const [d, setD] = useState(null);
  const [fund, setFund] = useState(false);
  const load = useCallback(() => entApi.get("/wallet").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const exportLedger = async (kind) => {
    const header = ["Date", "Type", "Category", "Description", "Amount", "Balance After", "Reference"];
    const body = (d.transactions || []).map((t) => [
      t.timestamp ? new Date(t.timestamp).toLocaleString() : "", t.type, t.category, t.description,
      (t.type === "credit" ? "" : "-") + t.amount, t.balance_after ?? "", t.reference || "",
    ]);
    const rows = [header, ...body];
    const stamp = Date.now();
    try {
      if (kind === "csv") downloadCSV(`topit-enterprise-wallet-ledger-${stamp}.csv`, rows);
      else await downloadXLSX(`topit-enterprise-wallet-ledger-${stamp}.xlsx`, "Wallet Ledger", rows);
      toast(`Exported ${body.length} ledger entr${body.length === 1 ? "y" : "ies"}`);
    } catch { toast("Export failed", false); }
  };
  if (!d) return <Loader />;
  return (
    <div className="space-y-6" data-testid="ent-wallet">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-slate-300 text-sm">Enterprise Wallet Balance</p>
          <p className="text-4xl font-extrabold mt-1" data-testid="ent-wallet-page-balance">{naira(d.balance)}</p>
          <div className="flex gap-2 mt-3">
            <span className="bg-white/10 px-2.5 py-1 rounded-lg text-xs">Airtime discount −{d.discount.airtime_pct}%</span>
            <span className="bg-white/10 px-2.5 py-1 rounded-lg text-xs">Data discount −{d.discount.data_pct}%</span>
          </div>
        </div>
        {can(role, "owner") && (
          <button onClick={() => setFund(true)} data-testid="ent-fund-btn" className="bg-red-600 hover:bg-red-700 px-5 py-3 rounded-xl font-bold flex items-center gap-2"><Icon name="add" /> Fund Wallet</button>
        )}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-slate-900">Wallet Ledger</h3>
          {d.transactions.length > 0 && (
            <div className="flex gap-2">
              <button onClick={() => exportLedger("csv")} data-testid="ent-ledger-export-csv" className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-semibold flex items-center gap-1.5 hover:bg-slate-50"><Icon name="table_view" className="text-base" /> CSV</button>
              <button onClick={() => exportLedger("xlsx")} data-testid="ent-ledger-export-xlsx" className="px-3 py-1.5 rounded-lg bg-green-700 text-white text-sm font-semibold flex items-center gap-1.5 hover:bg-green-800"><Icon name="grid_on" className="text-base" /> Excel</button>
            </div>
          )}
        </div>
        <div className="divide-y divide-slate-100" data-testid="ent-wallet-ledger">
          {d.transactions.length === 0 && <p className="px-5 py-10 text-center text-slate-400">No wallet activity yet.</p>}
          {d.transactions.map((t) => (
            <div key={t.id} className="px-5 py-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${t.type === "credit" ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
                <Icon name={t.type === "credit" ? "south_west" : "north_east"} className="text-lg" />
              </div>
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-800 truncate">{t.description}</p><p className="text-xs text-slate-400">{fmtDate(t.timestamp)} · {t.category}</p></div>
              <p className={`font-bold text-sm ${t.type === "credit" ? "text-green-600" : "text-slate-900"}`}>{t.type === "credit" ? "+" : "−"}{naira(t.amount)}</p>
            </div>
          ))}
        </div>
      </div>
      {fund && <FundModal flwConfigured={d.flw_configured} onClose={() => { setFund(false); load(); refreshHeader && refreshHeader(); }} />}
    </div>
  );
};

const FundModal = ({ flwConfigured, onClose }) => {
  const [amount, setAmount] = useState(10000);
  const [stage, setStage] = useState("input"); // input | pending | done
  const [txRef, setTxRef] = useState(null);
  const [msg, setMsg] = useState("");

  const initiate = async () => {
    try {
      const r = await entApi.post("/wallet/fund/initiate", { amount, redirect_url: `${window.location.origin}/payment-callback` });
      setTxRef(r.data.tx_ref);
      setStage("pending");
      if (r.data.public_key && window.FlutterwaveCheckout) {
        window.FlutterwaveCheckout({
          public_key: r.data.public_key,
          tx_ref: r.data.tx_ref,
          amount: Number(r.data.amount || amount),
          currency: "NGN",
          customer: { email: r.data.customer_email || "", name: r.data.customer_name || "" },
          customizations: { title: "TopIt Enterprise Wallet", description: `Fund wallet with ${naira(amount)}`, logo: window.location.origin + "/topit-flw-logo.png" },
          callback: () => { verify(); },
          onclose: () => { /* keep confirm panel open */ },
        });
      } else if (r.data.payment_link) {
        window.open(r.data.payment_link, "_blank");
      }
    } catch (e) { toast(e.response?.data?.detail || "Could not start payment", false); }
  };
  const verify = async () => {
    try {
      const r = await entApi.get(`/wallet/fund/verify/${txRef}`);
      if (r.data.success) { setStage("done"); setMsg(`Wallet credited! New balance ${naira(r.data.balance)}`); }
      else setMsg(r.data.message || "Payment not completed yet. Try again in a moment.");
    } catch { setMsg("Verification failed. Try again."); }
  };

  return (
    <Modal title="Fund Enterprise Wallet" onClose={onClose} testid="ent-fund-modal">
      {!flwConfigured && <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-xl p-3 mb-3">Online funding isn't enabled. Ask your TopIt admin to credit the wallet directly.</div>}
      {stage === "input" && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {[5000, 10000, 50000].map((a) => (
              <button key={a} onClick={() => setAmount(a)} data-testid={`ent-fund-preset-${a}`} className={`py-2.5 rounded-xl text-sm font-bold border ${amount === a ? "border-red-500 bg-red-50 text-red-700" : "border-slate-200 text-slate-700"}`}>{nairaK(a)}</button>
            ))}
          </div>
          <input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} data-testid="ent-fund-amount" className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
          <button onClick={initiate} disabled={!flwConfigured} data-testid="ent-fund-pay" className="w-full h-11 bg-red-600 text-white rounded-xl font-bold disabled:opacity-50">Pay {naira(amount)} with Card / Bank</button>
        </div>
      )}
      {stage === "pending" && (
        <div className="text-center space-y-3">
          <Icon name="lock" className="text-4xl text-red-600" />
          <p className="text-sm text-slate-600">A secure Flutterwave checkout has opened. After paying {naira(amount)}, confirm below.</p>
          {msg && <p className="text-sm text-amber-600">{msg}</p>}
          <button onClick={verify} data-testid="ent-fund-verify" className="w-full h-11 bg-slate-900 text-white rounded-xl font-bold">I've completed payment</button>
        </div>
      )}
      {stage === "done" && (
        <div className="text-center space-y-3">
          <Icon name="check_circle" className="text-5xl text-green-600" />
          <p className="font-semibold text-slate-800">{msg}</p>
          <button onClick={onClose} className="w-full h-11 bg-red-600 text-white rounded-xl font-bold">Done</button>
        </div>
      )}
    </Modal>
  );
};

// ==================== SCHEDULES ====================
const FREQS = [["once", "One-time"], ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]];
const DOW = [["1", "Mon"], ["2", "Tue"], ["3", "Wed"], ["4", "Thu"], ["5", "Fri"], ["6", "Sat"], ["0", "Sun"]];

const SchedulesTab = ({ role }) => {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const editable = can(role, "manager");
  const load = useCallback(() => entApi.get("/schedules").then((r) => setItems(r.data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (s) => { await entApi.patch(`/schedules/${s.id}`, { is_active: !s.is_active }); load(); };
  const del = async (id) => { if (!window.confirm("Delete this schedule?")) return; await entApi.delete(`/schedules/${id}`); load(); toast("Schedule deleted"); };

  return (
    <div className="space-y-4" data-testid="ent-schedules">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Auto top-ups run from your wallet at the scheduled time. Reminders just notify you.</p>
        {editable && <button onClick={() => setCreating(true)} data-testid="ent-add-schedule" className="bg-red-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-1.5"><Icon name="add" className="text-base" /> New Schedule</button>}
      </div>
      {!items ? <Loader /> : (
        <div className="grid gap-3" data-testid="ent-schedule-list">
          {items.length === 0 && <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400">No schedules yet.</div>}
          {items.map((s) => (
            <div key={s.id} data-testid={`ent-schedule-${s.id}`} className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-bold text-slate-900">{s.name}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.schedule_type === "auto" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>{s.schedule_type === "auto" ? "Auto top-up" : "Reminder"}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{s.is_active ? "Active" : "Paused"}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1 capitalize">{s.frequency} · {naira(s.amount)} {s.service_type} · {s.member_ids.length} member(s)</p>
                <p className="text-xs text-slate-400 mt-0.5">Next run: {fmtDate(s.next_execution)}{s.last_execution ? ` · Last: ${fmtDate(s.last_execution)}` : ""}</p>
              </div>
              {editable && (
                <div className="flex items-center gap-2 self-start md:self-center">
                  <button onClick={() => toggle(s)} data-testid={`ent-schedule-toggle-${s.id}`} className="px-3 py-2 border border-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-50">{s.is_active ? "Pause" : "Resume"}</button>
                  <button onClick={() => del(s.id)} data-testid={`ent-schedule-del-${s.id}`} className="text-slate-400 hover:text-red-600 p-2"><Icon name="delete" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {creating && <ScheduleModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); toast("Schedule created"); }} />}
    </div>
  );
};

const ScheduleModal = ({ onClose, onSaved }) => {
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [plans, setPlans] = useState({});
  const [f, setF] = useState({ name: "", schedule_type: "auto", service_type: "airtime", frequency: "weekly", amount: 200, day_of_week: 1, day_of_month: 1, time_of_day: "09:00", network: "MTN", subcode: null });
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    entApi.get("/members").then((r) => setMembers(r.data.items)).catch(() => {});
    entApi.get("/departments").then((r) => setDepartments(r.data.items)).catch(() => {});
    axios.get(`${API}/subscriptions`).then((r) => setPlans(r.data.providers || {})).catch(() => {});
  }, []);

  const networkFilter = f.service_type === "data" ? f.network : null;
  const visibleSelected = selected.filter((id) => { const m = members.find((x) => x.id === id); return m && (!networkFilter || m.provider === networkFilter); });
  const dataPlans = f.service_type === "data" ? (plans[f.network]?.data?.plans || []) : [];

  const save = async () => {
    if (visibleSelected.length === 0) return toast("Select members", false);
    if (f.service_type === "data" && !f.subcode) return toast("Select a data plan", false);
    setBusy(true);
    try {
      await entApi.post("/schedules", {
        name: f.name || "Scheduled top-up", schedule_type: f.schedule_type, service_type: f.service_type,
        member_ids: visibleSelected, amount: Number(f.amount), subcode: f.service_type === "data" ? f.subcode : null,
        network: f.service_type === "data" ? f.network : null, frequency: f.frequency,
        day_of_week: f.frequency === "weekly" ? Number(f.day_of_week) : null,
        day_of_month: f.frequency === "monthly" ? Number(f.day_of_month) : null, time_of_day: f.time_of_day,
      });
      onSaved();
    } catch (e) { toast(e.response?.data?.detail || "Failed", false); } finally { setBusy(false); }
  };

  return (
    <Modal title="New Schedule" onClose={onClose} testid="ent-schedule-modal">
      <div className="space-y-3">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ent-sched-name" placeholder="Schedule name (e.g. Weekly sales airtime)" className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
        <div className="grid grid-cols-2 gap-2">
          <select value={f.schedule_type} onChange={(e) => setF({ ...f, schedule_type: e.target.value })} data-testid="ent-sched-type" className="h-11 px-3 rounded-xl border border-slate-200 outline-none"><option value="auto">Auto top-up (charge wallet)</option><option value="reminder">Reminder only</option></select>
          <select value={f.service_type} onChange={(e) => setF({ ...f, service_type: e.target.value, subcode: null })} data-testid="ent-sched-service" className="h-11 px-3 rounded-xl border border-slate-200 outline-none"><option value="airtime">Airtime</option><option value="data">Data</option></select>
        </div>
        {f.service_type === "airtime" ? (
          <input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="ent-sched-amount" placeholder="Amount per member" className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <select value={f.network} onChange={(e) => setF({ ...f, network: e.target.value, subcode: null })} data-testid="ent-sched-network" className="h-11 px-3 rounded-xl border border-slate-200 outline-none">{PROVIDERS.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}</select>
            <select value={f.subcode || ""} onChange={(e) => { const pl = dataPlans.find((x) => x.subCode === e.target.value); setF({ ...f, subcode: e.target.value, amount: pl?.amount || 0 }); }} data-testid="ent-sched-plan" className="h-11 px-3 rounded-xl border border-slate-200 outline-none"><option value="">Select plan</option>{dataPlans.map((pl, i) => <option key={i} value={pl.subCode}>{pl.description} · {naira(pl.amount)}</option>)}</select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <select value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })} data-testid="ent-sched-freq" className="h-11 px-3 rounded-xl border border-slate-200 outline-none">{FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          <input type="time" value={f.time_of_day} onChange={(e) => setF({ ...f, time_of_day: e.target.value })} data-testid="ent-sched-time" className="h-11 px-3 rounded-xl border border-slate-200 outline-none" />
        </div>
        {f.frequency === "weekly" && <select value={f.day_of_week} onChange={(e) => setF({ ...f, day_of_week: e.target.value })} data-testid="ent-sched-dow" className="w-full h-11 px-3 rounded-xl border border-slate-200 outline-none">{DOW.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>}
        {f.frequency === "monthly" && <input type="number" min="1" max="28" value={f.day_of_month} onChange={(e) => setF({ ...f, day_of_month: e.target.value })} data-testid="ent-sched-dom" placeholder="Day of month (1-28)" className="w-full h-11 px-3 rounded-xl border border-slate-200 outline-none" />}
        <div>
          <p className="text-sm font-semibold text-slate-700 mb-1">Members ({visibleSelected.length} selected)</p>
          <MemberPicker members={members} departments={departments} selected={selected}
            onToggle={(id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id])}
            onToggleDept={(ids, add) => setSelected((s) => add ? [...new Set([...s, ...ids])] : s.filter((x) => !ids.includes(x)))}
            networkFilter={networkFilter} />
        </div>
        <button onClick={save} disabled={busy} data-testid="ent-sched-save" className="w-full h-11 bg-red-600 text-white rounded-xl font-bold disabled:opacity-50">{busy ? "Saving…" : "Create schedule"}</button>
      </div>
    </Modal>
  );
};

// ==================== TEAM ====================
const ROLE_BADGE = { owner: "bg-red-100 text-red-700", manager: "bg-blue-100 text-blue-700", viewer: "bg-slate-100 text-slate-600" };

const TeamTab = ({ role }) => {
  const [data, setData] = useState(null);
  const [invite, setInvite] = useState({ email: "", name: "", role: "viewer" });
  const [lastLink, setLastLink] = useState(null);
  const isOwner = role === "owner";
  const load = useCallback(() => entApi.get("/team").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const sendInvite = async () => {
    try {
      const r = await entApi.post("/team/invite", invite);
      setLastLink(r.data);
      setInvite({ email: "", name: "", role: "viewer" });
      load();
      toast(r.data.emailed ? "Invite emailed" : "Invite link created");
    } catch (e) { toast(e.response?.data?.detail || "Failed", false); }
  };
  const copyLink = (link) => { navigator.clipboard.writeText(link); toast("Invite link copied"); };
  const changeRole = async (u, newRole) => { try { await entApi.patch(`/team/${u.id}`, { role: newRole }); load(); toast("Role updated"); } catch (e) { toast(e.response?.data?.detail || "Failed", false); } };
  const removeUser = async (u) => { if (!window.confirm(`Remove ${u.name}?`)) return; try { await entApi.delete(`/team/${u.id}`); load(); toast("Member removed"); } catch (e) { toast(e.response?.data?.detail || "Failed", false); } };
  const revoke = async (id) => { await entApi.delete(`/team/invites/${id}`); load(); toast("Invite revoked"); };

  if (!data) return <Loader />;
  return (
    <div className="space-y-6" data-testid="ent-team">
      {isOwner && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="ent-invite-form">
          <h3 className="font-bold text-slate-900 mb-3">Invite a team member</h3>
          <div className="grid sm:grid-cols-4 gap-2">
            <input value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} data-testid="ent-invite-name" placeholder="Name" className="h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
            <input value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="ent-invite-email" placeholder="Email" className="h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
            <select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} data-testid="ent-invite-role" className="h-11 px-3 rounded-xl border border-slate-200 outline-none"><option value="viewer">Viewer</option><option value="manager">Manager</option></select>
            <button onClick={sendInvite} data-testid="ent-invite-send" className="h-11 bg-red-600 text-white rounded-xl font-semibold">Send invite</button>
          </div>
          {lastLink && (
            <div className="mt-3 bg-slate-50 rounded-xl p-3 flex items-center gap-2" data-testid="ent-invite-link-box">
              <Icon name="link" className="text-slate-400" />
              <input readOnly value={lastLink.invite_link} className="flex-1 bg-transparent text-sm text-slate-600 outline-none" />
              <button onClick={() => copyLink(lastLink.invite_link)} data-testid="ent-invite-copy" className="px-3 py-1.5 bg-slate-900 text-white rounded-lg text-sm font-semibold">Copy</button>
            </div>
          )}
          {lastLink && !lastLink.emailed && <p className="text-xs text-amber-600 mt-2">Email delivery isn't configured yet — share the link above. It'll auto-email once Resend is set up.</p>}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-bold text-slate-900">Team members</h3></div>
        <div className="divide-y divide-slate-100" data-testid="ent-team-list">
          {data.members.map((u) => (
            <div key={u.id} data-testid={`ent-team-${u.id}`} className="px-5 py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-sm">{(u.name || u.email)[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0"><p className="font-semibold text-slate-900 text-sm truncate">{u.name}{u.id === data.me && <span className="text-slate-400 font-normal"> (you)</span>}</p><p className="text-xs text-slate-400">{u.email}</p></div>
              {isOwner && u.id !== data.me ? (
                <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} data-testid={`ent-role-${u.id}`} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm"><option value="owner">Owner</option><option value="manager">Manager</option><option value="viewer">Viewer</option></select>
              ) : <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${ROLE_BADGE[u.role]}`}>{u.role}</span>}
              {isOwner && u.id !== data.me && <button onClick={() => removeUser(u)} data-testid={`ent-team-remove-${u.id}`} className="text-slate-400 hover:text-red-600 p-1"><Icon name="delete" /></button>}
            </div>
          ))}
        </div>
      </div>

      {isOwner && data.invites.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-bold text-slate-900">Pending invites</h3></div>
          <div className="divide-y divide-slate-100" data-testid="ent-invite-list">
            {data.invites.map((iv) => (
              <div key={iv.id} className="px-5 py-3 flex items-center gap-3">
                <Icon name="mail" className="text-slate-400" />
                <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-slate-800 truncate">{iv.email}</p><p className="text-xs text-slate-400 capitalize">{iv.role} · invited {fmtDate(iv.created_at)}</p></div>
                <button onClick={() => copyLink(`${window.location.origin}/enterprise/accept-invite?token=${iv.token}`)} className="text-slate-500 hover:text-slate-800 text-sm font-semibold px-2">Copy link</button>
                <button onClick={() => revoke(iv.id)} data-testid={`ent-invite-revoke-${iv.id}`} className="text-slate-400 hover:text-red-600 p-1"><Icon name="close" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== NOTIFICATIONS BELL ====================
const NotificationsBell = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = useCallback(() => entApi.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const openIt = async () => { setOpen(!open); if (!open && data.unread) { await entApi.post("/notifications/read"); setData((d) => ({ ...d, unread: 0 })); } };
  return (
    <div className="relative">
      <button onClick={openIt} data-testid="ent-bell" className="relative w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center text-slate-600">
        <Icon name="notifications" />
        {data.unread > 0 && <span data-testid="ent-bell-badge" className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{data.unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-slate-200 shadow-xl z-40 max-h-96 overflow-y-auto" data-testid="ent-notif-panel">
            <div className="px-4 py-3 border-b border-slate-100 font-bold text-slate-900 text-sm">Notifications</div>
            {data.items.length === 0 && <p className="px-4 py-8 text-center text-slate-400 text-sm">You're all caught up.</p>}
            {data.items.map((n) => (
              <div key={n.id} className="px-4 py-3 border-b border-slate-50 last:border-0">
                <div className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${n.level === "success" ? "bg-green-500" : n.level === "warning" ? "bg-amber-500" : "bg-blue-500"}`} /><p className="text-sm font-semibold text-slate-800">{n.title}</p></div>
                <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>
                <p className="text-[10px] text-slate-400 mt-1">{fmtDate(n.created_at)}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ==================== ACCEPT INVITE (public) ====================
const AcceptInvite = () => {
  const token = new URLSearchParams(window.location.search).get("token");
  const [info, setInfo] = useState(undefined);
  const [form, setForm] = useState({ name: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setInfo(null); return; }
    entApi.get(`/invite/${token}`).then((r) => { setInfo(r.data); setForm((f) => ({ ...f, name: r.data.name || "" })); }).catch(() => setInfo(null));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const r = await entApi.post(`/invite/${token}/accept`, form);
      localStorage.removeItem("topit_enterprise_token"); // session now in httpOnly cookie
      window.location.href = "/enterprise";
    } catch (err) { setError(err.response?.data?.detail || "Could not accept invite"); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(225,29,46,0.15), transparent 40%)" }}>
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl" data-testid="ent-accept-invite">
        <img src={APP_LOGO} alt="TopIt" className="h-9 mx-auto" />
        <p className="text-slate-400 text-[11px] tracking-wide mt-1.5 text-center">Enterprise · Powered by Sineli</p>
        {info === undefined && <Loader />}
        {info === null && <p className="text-center text-slate-600 mt-6" data-testid="ent-invite-invalid">This invitation is invalid or has expired. Please ask your team owner for a new invite.</p>}
        {info && (
          <>
            <p className="text-center text-slate-700 mt-4">Join <b>{info.enterprise_name}</b> as <b className="capitalize">{info.role}</b></p>
            <p className="text-center text-xs text-slate-400 mb-4">{info.email}</p>
            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-xl text-sm mb-3" data-testid="ent-accept-error">{error}</div>}
            <form onSubmit={accept} className="space-y-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ent-accept-name" placeholder="Your name" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="ent-accept-password" placeholder="Create a password (min 6 chars)" required className="w-full h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-red-500" />
              <button type="submit" disabled={busy} data-testid="ent-accept-submit" className="w-full h-11 bg-red-600 text-white rounded-xl font-bold disabled:opacity-50">{busy ? "Joining…" : "Accept & join"}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== SHELL ====================
const EnterpriseShell = ({ onLogout }) => {
  const [ctx, setCtx] = useState(null);
  const [tab, setTab] = useState(() => localStorage.getItem("topit_ent_tab") || "dashboard");
  const [navOpen, setNavOpen] = useState(false);

  const loadMe = useCallback(() => entApi.get("/me").then((r) => setCtx(r.data)).catch(() => onLogout()), [onLogout]);
  useEffect(() => { loadMe(); }, [loadMe]);
  useEffect(() => { localStorage.setItem("topit_ent_tab", tab); }, [tab]);

  if (!ctx) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Icon name="progress_activity" className="animate-spin text-4xl text-red-600" /></div>;
  const role = ctx.user.role;
  const goTo = (t) => { setTab(t); setNavOpen(false); };

  const Sidebar = (
    <>
      <div className="px-5 py-5 text-white" data-testid="ent-sidebar-logo">
        <img src={APP_LOGO_WHITE} alt="TopIt" className="h-7 w-auto object-contain" />
        <div className="flex items-center gap-1.5 mt-1.5">
          <span className="text-red-500 font-bold text-[11px] uppercase tracking-wide">Enterprise</span>
          <span className="text-slate-400 text-[9px] tracking-wide">· Powered by Sineli</span>
        </div>
      </div>
      <nav className="px-3 space-y-1 flex-1">
        {NAV.map((n) => (
          <button key={n.id} onClick={() => goTo(n.id)} data-testid={`ent-nav-${n.id}`}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab === n.id ? "bg-red-600 text-white" : "text-slate-300 hover:bg-white/5"}`}>
            <Icon name={n.icon} /> {n.label}
          </button>
        ))}
      </nav>
      <div className="px-3 pb-5 border-t border-white/10 pt-3 mt-3">
        <div className="px-4 py-2">
          <p className="text-sm font-semibold text-white truncate">{ctx.enterprise.name}</p>
          <p className="text-xs text-slate-400 truncate">{ctx.user.name} · <span className="capitalize text-red-400">{role}</span></p>
        </div>
        <button onClick={onLogout} data-testid="ent-logout" className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-300 hover:bg-white/5"><Icon name="logout" /> Logout</button>
      </div>
    </>
  );

  const label = NAV.find((n) => n.id === tab)?.label;
  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-[#0B1220] flex flex-col transition-transform ${navOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>{Sidebar}</aside>
      {navOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setNavOpen(false)} />}
      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button onClick={() => setNavOpen(true)} data-testid="ent-mobile-menu" className="lg:hidden text-slate-600"><Icon name="menu" /></button>
            <h1 className="text-lg font-bold text-slate-900">{label}</h1>
          </div>
          <div className="flex items-center gap-3">
            <NotificationsBell />
            <span className="hidden sm:inline-flex items-center gap-1.5 bg-slate-100 px-3 py-1.5 rounded-xl text-sm font-semibold text-slate-700" data-testid="ent-header-balance"><Icon name="account_balance_wallet" className="text-base text-red-600" /> {naira(ctx.enterprise.wallet_balance)}</span>
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-900">{ctx.enterprise.name}</p>
              <p className="text-xs text-slate-400 capitalize">{role}</p>
            </div>
          </div>
        </header>
        <div className="p-5 lg:p-8">
          {tab === "dashboard" && <DashboardTab role={role} goTo={goTo} refreshHeader={loadMe} />}
          {tab === "airtime" && <TopUpTab service="airtime" role={role} refreshHeader={loadMe} />}
          {tab === "data" && <TopUpTab service="data" role={role} refreshHeader={loadMe} />}
          {tab === "members" && <MembersTab role={role} />}
          {tab === "schedules" && <SchedulesTab role={role} />}
          {tab === "transactions" && <TransactionsTab />}
          {tab === "analytics" && <AnalyticsTab entName={ctx.enterprise.name} />}
          {tab === "team" && <TeamTab role={role} />}
          {tab === "wallet" && <WalletTab role={role} refreshHeader={loadMe} />}
        </div>
      </main>
    </div>
  );
};

export default function EnterpriseApp() {
  const isAcceptInvite = typeof window !== "undefined" && window.location.pathname.includes("/enterprise/accept-invite");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    if (isAcceptInvite) { setChecking(false); return; }
    // Session lives in an httpOnly cookie — validate via /me
    entApi.get("/me").then(() => setAuthed(true)).catch(() => {}).finally(() => setChecking(false));
  }, [isAcceptInvite]);
  const logout = async () => {
    try { await entApi.post("/logout"); } catch { /* noop */ }
    localStorage.removeItem("topit_enterprise_token");
    setAuthed(false);
  };
  if (isAcceptInvite) return <AcceptInvite />;
  if (checking) return <div className="min-h-screen flex items-center justify-center bg-slate-900"><Icon name="progress_activity" className="text-red-500 text-5xl animate-spin" /></div>;
  return authed ? <EnterpriseShell onLogout={logout} /> : <EnterpriseAuth onAuthed={() => setAuthed(true)} />;
}
